// A titled block of a screen: a small uppercase heading, an optional action beside it, an optional
// hint, then the content. Every group of related things on a screen is a Section.
import "./config";
import { styled } from "tamagui";
import { Icon, type IconName } from "./Icon";
import { Column, Row, type ColumnProps } from "./layout";
import { Caption, Eyebrow } from "./text";

const SectionTitle = styled(Eyebrow, {
  name: "SectionTitle",
  fontSize: "2",
  lineHeight: "2",
  color: "textDim",
  flexDirection: "row",
  alignItems: "center",
  gap: "2",
});

export interface SectionProps extends Omit<ColumnProps, "children"> {
  title?: string | undefined;
  /** A small icon before the title. */
  icon?: IconName | undefined;
  /** The heading level; sections nested in a section are 3. */
  level?: 2 | 3;
  /** One line of explanation under the heading. */
  hint?: string | undefined;
  /** What the section lets you do (a link, a small button), at the end of the heading line. */
  action?: React.ReactNode;
  children?: React.ReactNode;
}

export function Section({
  title,
  icon,
  level = 2,
  hint,
  action,
  children,
  ...rest
}: SectionProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <Column render="section" gap="3" {...rest}>
      {title === undefined && action === undefined ? null : (
        <Row spread gap="3">
          {title === undefined ? null : (
            <SectionTitle render={level === 2 ? "h2" : "h3"}>
              {icon === undefined ? null : <Icon name={icon} size={14} />}
              {title}
            </SectionTitle>
          )}
          {action}
        </Row>
      )}
      {hint === undefined ? null : <Caption lineHeight="4">{hint}</Caption>}
      {children}
    </Column>
  );
}
